import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute } from "@tanstack/react-router";
import { HelpfulPricingGuides } from "@/components/HelpfulPricingGuides";
import { Link } from "@tanstack/react-router";
import { CheckCircle, ArrowRight, AlertTriangle } from "lucide-react";
import { services } from "@/lib/site-config";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { HelpfulGuidesGrid } from "@/components/HelpfulGuidesGrid";
import { ServiceHero } from "@/components/ServiceHero";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { ProcessSteps } from "@/components/ProcessSteps";
import { CTABlock } from "@/components/CTABlock";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { seoHead, serviceSchema, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { DriverSupportCTA } from "@/components/DriverSupportCTA";
import { WhyChooseWoodseer } from "@/components/WhyChooseWoodseer";

const svc = services[3]; // mechanical repairs

const repairCategories = [
  { title: "Brakes", items: ["Brake pads and discs", "Caliper repair and replacement", "Brake fluid change", "Handbrake adjustment"] },
  { title: "Clutch & Gearbox", items: ["Clutch replacement", "Flywheel repair", "Gearbox diagnostics", "Gear linkage adjustment"] },
  { title: "Suspension & Steering", items: ["Springs and shock absorbers", "Anti-roll bar links", "Bushings and ball joints", "Steering rack and power steering"] },
  { title: "Engine & Cooling", items: ["Engine repairs and overhauls", "Timing belt/chain replacement", "Radiator and thermostat", "Water pump and hoses"] },
];

const extendedFaqs = [
  ...svc.faqs,
  { q: "Do you carry out timing belt replacements?", a: "Yes. Timing belts are a critical maintenance item — if they snap, the engine damage can be severe. We replace timing belts and chains at manufacturer-recommended intervals." },
  { q: "What if you find extra problems during a repair?", a: "We'll contact you before doing any additional work. You'll get a clear explanation and updated quote — nothing gets done without your say-so." },
  { q: "How much do typical repairs cost?", a: "Brake pads from £120 (fitted, per axle). Front discs and pads from £220. Clutch replacement from £550. Shock absorbers from £180 each fitted. Every job is quoted in writing before we start — no surprises." },
  { q: "How long do repairs take?", a: "Brakes and basic suspension: same day. Clutches and timing belts: usually one full day. Gearbox and engine work: 2–5 days depending on parts availability. We give a realistic completion date and call you the moment your car is ready." },
  { q: "What's included in a repair quote?", a: "Parts (specified by brand — Brembo, LuK, Bilstein, etc.), labour at our hourly rate, VAT, road test, and a 12-month parts-and-labour warranty. No diagnostic charge if the fault is obvious from the symptoms." },
];


export const Route = createFileRoute("/mechanical-repairs")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) => seoHead({
    path: "/mechanical-repairs",
    title: "Car Repairs East London E14 | Brakes, Clutch, Suspension | Woodseer",
    description: `Mechanical car repairs in East London E14 — brakes, clutch, gearbox, suspension. Written quote first, no work without approval. Call ${NAP.phone}.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Mechanical Repairs", path: "/mechanical-repairs" },
    ],
    jsonLd: [
      localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
      faqSchema(extendedFaqs),
      serviceSchema({
        name: "Mechanical Repairs — Woodseer Autos",
        serviceType: "Mechanical Repairs",
        description: "Brake, clutch, gearbox, suspension and engine repairs in East London — quality parts and clear quotes before work starts.",
        offers: { price: "120.00", priceCurrency: "GBP", description: "Brake pads from £120; clutch replacement from £550 — exact quote before any work." },
      }),
    ],
  }),
  component: RepairsPage,
});

function RepairsPage() {
  return (
    <>
      <ServiceHero
        headline={svc.headline}
        intro={svc.intro}
        image="/images/hero-repairs.jpg"
        imageAlt="Mechanical repairs at Woodseer Autos, East London"
      />

      <ReassuranceBanner />

      {/* Repair Categories */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">What We Repair</h2>
            <p className="mt-3 text-center text-muted-foreground">Practical copy, no fluff — here's what we handle day in, day out.</p>
            <StaggerContainer className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {repairCategories.map((cat) => (
                <StaggerItem key={cat.title}>
                  <div className="rounded-xl border border-border bg-background p-6">
                    <h3 className="text-lg font-bold text-foreground">{cat.title}</h3>
                    <ul className="mt-3 space-y-2">
                      {cat.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Warning signs */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Warning Signs to Watch For</h2>
            <p className="mt-3 text-center text-muted-foreground">If any of these sound familiar, don't leave it — get it checked.</p>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {svc.signs.map((sign) => (
                <StaggerItem key={sign}>
                  <div className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm text-muted-foreground bg-card/50">
                    <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {sign}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* What happens if you leave it */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-foreground">What Happens If You Leave It?</h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Ignoring mechanical issues rarely makes them go away — they usually get worse and more expensive. Worn brake pads become damaged discs. A slipping clutch becomes a failed clutch. A minor suspension knock becomes a failed MOT. Getting things checked early saves you time, money and stress.
            </p>
          </div>
        </section>
      </AnimatedSection>

      {/* Why choose us */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Why Choose Woodseer Autos</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {svc.whyUs.map((reason) => (
                <StaggerItem key={reason}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {reason}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <ProcessSteps steps={svc.process} title="How Repairs Work" />
      </AnimatedSection>

      <AnimatedSection>
      <HelpfulGuidesGrid slugs={["brake-noise-squeaking-grinding","car-pulling-to-one-side","tyre-check-before-mot"]} title="Helpful guides for this service" compact />

        <FAQAccordion faqs={extendedFaqs} />
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-2xl font-bold text-foreground text-center">Other Services</h2>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {services.filter((s) => s.slug !== "mechanical-repairs").map((s) => (
                <Link key={s.slug} to={`/${s.slug}` as any} className="flex flex-col items-center rounded-xl border border-border bg-background/50 p-4 hover:border-primary/40 transition-all text-center group">
                  <span className="text-sm font-semibold text-foreground">{s.name}</span>
                  <ArrowRight className="mt-1 h-3.5 w-3.5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Local areas */}
      <AnimatedSection>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-5xl px-4 text-center">
            <p className="text-sm text-muted-foreground">Mechanical repairs for drivers across{" "}
              <Link to="/areas/$area" params={{ area: "poplar" }} className="text-primary hover:text-primary/80">Poplar</Link>,{" "}
              <Link to="/areas/$area" params={{ area: "canary-wharf" }} className="text-primary hover:text-primary/80">Canary Wharf</Link>,{" "}
              <Link to="/areas/$area" params={{ area: "limehouse" }} className="text-primary hover:text-primary/80">Limehouse</Link>,{" "}
              <Link to="/areas/$area" params={{ area: "docklands" }} className="text-primary hover:text-primary/80">Docklands</Link> and{" "}
              <Link to="/areas/$area" params={{ area: "tower-hamlets" }} className="text-primary hover:text-primary/80">Tower Hamlets</Link>.
            </p>
          </div>
        </section>
      </AnimatedSection>

      <HelpfulPricingGuides currentService="mechanical-repairs" />
      <div className="mx-auto max-w-4xl px-4"><DriverSupportCTA page="mechanical-repairs" service="mechanical-repairs" bookingContext="mech-repairs-driver-support" /></div>
      <WhyChooseWoodseer page="mechanical-repairs" />
      <CTABlock headline="Something Not Right?" subtext="Noises, vibrations, warning lights — don't ignore them. Call us and we'll find out what's going on." />
    </>
  );
}
